import React, { useEffect, useId, useRef, useState } from "react";

// import { useNavigate } from "react-router-dom";
import { useNavigate } from "../../../components/Navigate";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPencil } from "@fortawesome/free-solid-svg-icons";
import { faCopy, faShieldBlank } from "@fortawesome/free-solid-svg-icons";

import config from "../../../config.json";
import timeZoneData from '../../dev/data/timezones';
import { ProfileUpdateTypeEnum } from "../types/ProfileUpdateType.enum";
import ButtonControl from "../../../components/controls/ButtonControl";

import "./AccountSettingsDetails.scss";
import { useAppUserManager, useUserManager } from "../../../hooks/useManagers";
import { APIStatusType } from "../../../stores/globalAppUserStore";
import { DialogControl } from "../../../components/controls/DialogControl";
import { SelectPicker } from "rsuite";

const AccountSettingsDetails = ({
  eePassId,
  timeZoneAbbreviation,
  passwordResetOn,
  mobilePhone,
  isMfaEnabled,
  setProfileUpdateType,

}) => {
  const { instance } = useMsal();
  const navigate = useNavigate();
  const styles = { width: '100%', display: 'block', marginBottom: 10 };
  const [passwordResetOnText, setPasswordResetOnText] = useState("");
  const [timeZone, setTimeZone] = useState("");
  const [copyStatus, setCopyStatus] = useState("idle");
  const [copyHelpVisible, setCopyHelpVisible] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [selectedTimeZone, setSelectedTimeZone] = useState(null);
  const [savingTimezone, setSavingTimezone] = useState(false);
  const appUserManager = useAppUserManager();
  const userManager = useUserManager();
  const [openDialog, setOpenDialog] = useState(false);
  const [errors, setErrors] = useState(null);
  const copyHelpId = useId();
  const timeZoneLabelId = useId();
  const copyStatusTimeoutRef = useRef(null);
  const copyAttemptRef = useRef(0);
  const mountedRef = useRef(true);

  const clearCopyStatusTimeout = () => {
    if (copyStatusTimeoutRef.current) {
      clearTimeout(copyStatusTimeoutRef.current);
      copyStatusTimeoutRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      mountedRef.current = false;
      copyAttemptRef.current += 1;
      clearCopyStatusTimeout();
    };
  }, []);

  useEffect(() => {
    if (appUserManager) {
      setIsAuthenticated(appUserManager.GetGlobalState().apiStatus === APIStatusType.OnlineSecure);
    }
  }, [appUserManager]);

  useEffect(() => {
    if (!openDialog) {
      setSelectedTimeZone(timeZoneAbbreviation);
    }
  }, [openDialog, timeZoneAbbreviation]);

  useEffect(() => {
    if (passwordResetOn) {
      const datePasswordReset = new Date(0);
      datePasswordReset.setUTCSeconds(passwordResetOn);

      const dateFormatted = `${datePasswordReset.getMonth() + 1
        }/${datePasswordReset.getDate()}/${datePasswordReset.getFullYear()}`;

      const today = new Date();
      const differenceInTime = today - datePasswordReset.getTime();

      const differenceInDays = Math.floor(
        differenceInTime / (1000 * 3600 * 24)
      );

      const wasChangedToday =
        today.getMonth() === datePasswordReset.getMonth() &&
        today.getDate() === datePasswordReset.getDate() &&
        today.getFullYear() === datePasswordReset.getFullYear();

      if (wasChangedToday === true) {
        setPasswordResetOnText(`Last changed today (${dateFormatted}).`);
      } else {
        setPasswordResetOnText(
          `Last changed ${differenceInDays} days ago (${dateFormatted}).`
        );
      }
    }

    updateTimeZone(timeZoneAbbreviation);

  }, [passwordResetOn, timeZoneAbbreviation]);

  const updateTimeZone = (timeZoneAbbr) => {
    if (timeZoneAbbr) {
      switch (timeZoneAbbr) {
        case "EST":
          setTimeZone("EST (Eastern Standard Time)");
          break;
        case "PST":
          setTimeZone("PST (Pacific Standard Time)");
          break;
        case "CST":
          setTimeZone("CST (Central Standard Time)");
          break;
        case "MST":
          setTimeZone("MST (Mountain Standard Time)");
          break;
        case "AST":
          setTimeZone("AST (Atlantic Standard Time)");
          break;
        case "HST":
          setTimeZone("HST (Hawaii Standard Time)");
          break;
        case "AZ":
          setTimeZone("AZ (AZ Time Zone)");
          break;
        default:
          setTimeZone("");
          break;
      }
    }
  }

  const handleOnSave = async () => {
    setSavingTimezone(true);
    setErrors(null);
    try {
      const id = appUserManager.AppUserState.userProfile.Id;
      const userProfileResult = await userManager.get(id);

      if (userProfileResult?.Success) {
        const userProfile = userProfileResult.Items.first();
        if (userProfile?.TimeZone !== selectedTimeZone) {
          userProfile.TimeZone = selectedTimeZone;
          const result = await userManager.ItemManager.runOperation('AzureB2CSaveUserProfile', undefined, userProfile, undefined, undefined, true);
          if (result?.Success) {
            updateTimeZone(selectedTimeZone);
            window.location.href = window.location.href;
            setSavingTimezone(false);
          } else {
            setErrors(['Failed to save timezone.']);
            setSavingTimezone(false);
          }
        } else {
          setSavingTimezone(false);
          setOpenDialog(false);
        }
      } else {
        setErrors(['Failed to load user profile and save timezone.']);
        setSavingTimezone(false);
      }
    } catch (error) {
      setErrors(['Failed to save timezone.']);
      setSavingTimezone(false);
    }
  }

  const onEditTimeZone = () => {
    setErrors(null);
    setSelectedTimeZone(timeZoneAbbreviation);
    setOpenDialog(true);
  }

  const handleOnCancel = () => {
    setErrors(null);
    setSelectedTimeZone(timeZoneAbbreviation);
    setOpenDialog(false);
  }

  const handleOnTimezoneChange = (value) => {
    setSelectedTimeZone(value);
  }


  // Change Password redirects to an Azure AD B2C-hosted policy page (external
  // configuration boundary). This component is only responsible for the
  // semantic, keyboard-operable trigger and the existing redirect/cancel
  // behavior below; the hosted form's own markup/labels/contrast are outside
  // this application's codebase and are not modified by this change. No
  // hosted-page accessibility defect was identified in this audit; any future
  // finding there would require a separately approved Azure AD B2C change.
  const onChangePasswordClicked = () => {
    if (isAuthenticated === false) {
      navigate("/logout");
    } else if (isAuthenticated) {
      setProfileUpdateType(ProfileUpdateTypeEnum.PROFILE_CHANGE_PASSWORD);
      instance.loginRedirect({
        redirectUri: window.location.href,
        extraQueryParameters: {
          p: config.AUTH.ADB2C_CHANGE_PASSWORD_POLICY,
          cancelUrl: encodeURIComponent(window.location.href),
        },
      });
    }
  };

  const onConfigureMfaClicked = () => {
    if (isAuthenticated === false) {
      navigate("/logout");
    } else if (isAuthenticated) {
      setProfileUpdateType(ProfileUpdateTypeEnum.PROFILE_CONFIGURE_MFA);
      instance.loginRedirect({
        redirectUri: window.location.href,
        extraQueryParameters: {
          p: config.AUTH.ADB2C_CONFIGURE_MFA_POLICY,
          cancelUrl: encodeURIComponent(window.location.href),
        },
      });
    }
  };

  const displayCopyToClipboard = () => {
    if (navigator.clipboard) return <FontAwesomeIcon icon={faCopy} aria-hidden="true" />;
    return null;
  };

  const showCopyStatus = (status, attempt) => {
    if (!mountedRef.current || attempt !== copyAttemptRef.current) return;

    setCopyStatus(status);
    copyStatusTimeoutRef.current = setTimeout(() => {
      if (mountedRef.current && attempt === copyAttemptRef.current) {
        setCopyStatus("idle");
        copyStatusTimeoutRef.current = null;
      }
    }, 4000);
  };

  const copyIdToClipboard = async () => {
    clearCopyStatusTimeout();
    setCopyStatus("idle");
    setCopyHelpVisible(false);
    const attempt = copyAttemptRef.current + 1;
    copyAttemptRef.current = attempt;

    if (!navigator.clipboard || !navigator.clipboard.writeText) {
      showCopyStatus("error", attempt);
      return;
    }

    try {
      await navigator.clipboard.writeText(eePassId);
      showCopyStatus("success", attempt);
    } catch (error) {
      showCopyStatus("error", attempt);
    }
  };

  const handleCopyKeyDown = (event) => {
    if (event.key === "Escape") setCopyHelpVisible(false);
  };

  const loadStrengthenYourSecurity = () => {
    return <div className="account-settings__strengthen-security">
      <i>
        {!isMfaEnabled && <>Multi-Factor Authentication is used to strengthen the security of your account, by using your mobile phone to confirm your identity. </>} Please note, by changing your MFA status you will be prompted to log back into the application.
      </i>
    </div>
  };

  return (
    <>
      <DialogControl openDialog={openDialog} onCancel={handleOnCancel} loadingOk={savingTimezone} title={'Please choose a new Time Zone.'} onOk={handleOnSave} excludeCancel={false}>
        <div style={{ paddingBottom: '2.12em' }}>
          {errors &&
            <div className="error account-settings__dialog-error" role="alert">{errors.first()}</div>
          }
          <label id={timeZoneLabelId} className="account-settings__timezone-label">
            Time Zone
          </label>
          <SelectPicker className={'narrow-dropdown'} searchable={false} onChange={handleOnTimezoneChange}
            aria-labelledby={timeZoneLabelId}
            placeholder={'Select a Time Zone'} size='md' styles={styles} value={selectedTimeZone} data={timeZoneData} />
          <br /><br />
          <h5><i>Please note, this will cause the page to refresh once when save is completed.</i></h5>
        </div>
      </DialogControl >
      <div className="screen-header account-settings__heading-container">
        <FontAwesomeIcon className="fa-xl" icon={faShieldBlank} />
        <h2 className="account-settings__heading">Account Settings</h2>
      </div>

      <div className="field-input account-settings__main">
        <div className="generic-input-component">
          <div className="account-settings__field-main">
            <div className="title">
              <div>EE PASS #</div>
            </div>
            <div className="account-settings__field-value account-settings__copy-control">
              <span>{eePassId}</span>
              <button
                type="button"
                className="account-settings__copy-ee-pass-id"
                aria-label="Copy EE PASS ID"
                aria-describedby={copyHelpId}
                onClick={copyIdToClipboard}
                onFocus={() => setCopyHelpVisible(true)}
                onBlur={() => setCopyHelpVisible(false)}
                onMouseEnter={() => setCopyHelpVisible(true)}
                onMouseLeave={() => setCopyHelpVisible(false)}
                onKeyDown={handleCopyKeyDown}
              >
                {displayCopyToClipboard()}
              </button>
              <span
                id={copyHelpId}
                className={`account-settings__copy-help${copyHelpVisible && copyStatus === "idle" ? " account-settings__copy-help--visible" : ""}`}
                role="tooltip"
              >
                Copy ID
              </span>
              {copyStatus === "success" && (
                <span className="account-settings__copy-status" role="status">
                  EE PASS ID copied.
                </span>
              )}
              {copyStatus === "error" && (
                <span
                  className="account-settings__copy-status account-settings__copy-status--error"
                  role="alert"
                >
                  Unable to copy the EE PASS ID. Please copy it manually.
                </span>
              )}
            </div>
          </div>
          <div className="account-settings__field-main">
            <div className="title">
              <div>Time Zone</div>
            </div>
            <div className="account-settings__field-value">{timeZone}
              <button
                type="button"
                className="profile__field-edit"
                aria-label="Edit Time Zone"
                onClick={onEditTimeZone}
              >
                <FontAwesomeIcon
                  className="btn-icon-fa"
                  icon={faPencil}
                  aria-hidden="true"
                />
              </button>
            </div>
          </div>
        </div>
      </div>

      <h4 className="account-settings__subheading">Password</h4>
      {/* Status text, not a document heading: rendering it as a heading (previously
          <h6>) both skipped from h4 to h6 (heading-order) and produced an empty
          heading whenever passwordResetOnText has not been populated yet
          (empty-heading), both flagged by an axe scan (Phase 7.2). */}
      {passwordResetOnText && (
        <p className="account-settings__subheading-desc-password">
          {passwordResetOnText}
        </p>
      )}
      <div className="account-settings__button">
        <div className="btn-wrapper-right">
          <ButtonControl type={'sub-action'} onClick={onChangePasswordClicked}>
            Change Password
          </ButtonControl>
        </div>
      </div>

      <h4 className="account-settings__subheading">
        Multi-Factor Authentication (MFA)
      </h4>
      <div className="account-settings__subheading-desc-mfa">
        MFA is currently turned&nbsp;
        <span className="account-settings__subheading-desc-mfa-is-on">
          {isMfaEnabled ? "ON" : "OFF"}
        </span>
        .
      </div>
      <div className="field-input">
        <div className="generic-input-component">
          {isMfaEnabled && <div className="account-settings__field-mfa">
            <div className="title">
              <div>Linked Mobile</div>
            </div>
            <div className="account-settings__field-value">{mobilePhone}</div>
          </div>}
        </div>
      </div>
      {loadStrengthenYourSecurity()}
      <div className="account-settings__button">
        <div className="btn-wrapper-right">
          <ButtonControl type={'sub-action'} onClick={onConfigureMfaClicked}>
            Configure MFA
          </ButtonControl>
        </div>
      </div>
    </>
  );
};

export default AccountSettingsDetails;
